import { FC, useState } from 'react';
import ConfirmModal from '@/components/seop/ConfirmModal';
import { useToast } from '@/contexts/ToastContext';
import { seopService } from '@/services/seop.service';
import type { SeopPhysicalResource, SeopRenameImpact, SeopResource } from '@/types/seop';

const INPUT = 'w-full rounded-lg border border-theme-border bg-surface-secondary px-2 py-1 text-sm';
const CELL = 'py-2 pr-3 text-sm text-content-primary';

/** Disponibilidade default do formulário de máquina, como no app. */
const DISPONIBILIDADE_PADRAO = 80;

interface RenameEmCurso {
  recurso: SeopResource;
  novo: string;
  impacto: SeopRenameImpact | null;
}

interface EditorDeRecursosProps {
  recursos: SeopResource[];
  fisicos: SeopPhysicalResource[];
  onRecarregar: () => Promise<void>;
  onExcluirRecurso: (recurso: SeopResource) => void;
}

/** A primeira mensagem legível de um erro do DRF, inclusive o 409 do rename. */
function mensagemDoErro(err: unknown): string | null {
  const dados = (err as { response?: { data?: { error?: unknown; detail?: unknown } } })?.response?.data;
  const detalhe = (dados as { detail?: unknown })?.detail;
  if (Array.isArray(detalhe) && typeof detalhe[0] === 'string') return detalhe.join(' ');
  if (typeof detalhe === 'string') return detalhe;
  return null;
}

/**
 * Cadastro de recursos: os de balanceamento (que recebem carga e têm capacidade) e as
 * máquinas físicas.
 *
 * Fora da página porque a tela de Cadastros já carrega quatro blocos, e este tem o único
 * fluxo com confirmação de impacto — renomear um recurso alcança rotas, lotes, alavancas,
 * custos, alocações e cenários salvos, já que a referência é por NOME e não por FK.
 */
const EditorDeRecursos: FC<EditorDeRecursosProps> = ({ recursos, fisicos, onRecarregar, onExcluirRecurso }) => {
  const { showToast } = useToast();
  const [renomeando, setRenomeando] = useState<RenameEmCurso | null>(null);
  const [salvando, setSalvando] = useState(false);
  const [novoRecurso, setNovoRecurso] = useState({ name: '', critical: '' });
  const [erroNovo, setErroNovo] = useState<string | null>(null);
  const [novaMaquina, setNovaMaquina] = useState({
    name: '',
    resource_group: '',
    critical: '',
    availability: String(DISPONIBILIDADE_PADRAO),
  });

  const salvarCampo = async (recurso: SeopResource, campo: 'critical' | 'order', valor: string) => {
    const dados = campo === 'order' ? { order: Number(valor) || 0 } : { critical: valor };
    try {
      await seopService.updateResource(recurso.id, dados);
      await onRecarregar();
    } catch (e) {
      showToast(mensagemDoErro(e) ?? 'Erro ao salvar o recurso.', 'error');
    }
  };

  /** O rename não salva no blur: pede confirmação com o impacto medido no servidor. */
  const pedirRename = async (recurso: SeopResource, novo: string) => {
    const limpo = novo.trim();
    if (!limpo || limpo === recurso.name) return;
    setRenomeando({ recurso, novo: limpo, impacto: null });
    try {
      setRenomeando({ recurso, novo: limpo, impacto: await seopService.getRenameImpact(recurso.id) });
    } catch {
      // Sem o impacto a confirmação ainda vale — ela só fica sem os números.
    }
  };

  const confirmarRename = async () => {
    if (!renomeando) return;
    setSalvando(true);
    try {
      await seopService.updateResource(renomeando.recurso.id, { name: renomeando.novo });
      await onRecarregar();
      setRenomeando(null);
    } catch (e) {
      showToast(mensagemDoErro(e) ?? 'Erro ao renomear o recurso.', 'error');
    } finally {
      setSalvando(false);
    }
  };

  const adicionarRecurso = async () => {
    setErroNovo(null);
    if (!novoRecurso.name.trim()) {
      setErroNovo('Dê um nome ao recurso.');
      return;
    }
    try {
      await seopService.createResource({
        name: novoRecurso.name.trim(),
        critical: novoRecurso.critical.trim(),
        order: recursos.length,
      });
      setNovoRecurso({ name: '', critical: '' });
      await onRecarregar();
    } catch (e) {
      setErroNovo(mensagemDoErro(e) ?? 'Erro ao cadastrar o recurso.');
    }
  };

  const salvarMaquina = async (maquina: SeopPhysicalResource, campo: string, valor: string) => {
    const dados = campo === 'availability' ? { availability: Number(valor) / 100 } : { [campo]: valor };
    try {
      await seopService.updatePhysicalResource(maquina.id, dados);
      await onRecarregar();
    } catch (e) {
      showToast(mensagemDoErro(e) ?? 'Erro ao salvar a máquina.', 'error');
    }
  };

  const excluirMaquina = async (maquina: SeopPhysicalResource) => {
    try {
      await seopService.deletePhysicalResource(maquina.id);
      await onRecarregar();
    } catch (e) {
      showToast(mensagemDoErro(e) ?? 'Erro ao excluir a máquina.', 'error');
    }
  };

  const adicionarMaquina = async () => {
    if (!novaMaquina.name.trim()) {
      showToast('Dê um nome à máquina.', 'error');
      return;
    }
    try {
      await seopService.createPhysicalResource({
        name: novaMaquina.name.trim(),
        resource_group: novaMaquina.resource_group.trim(),
        critical: novaMaquina.critical.trim(),
        availability: Number(novaMaquina.availability) / 100,
        order: fisicos.length,
      });
      setNovaMaquina({ name: '', resource_group: '', critical: '', availability: String(DISPONIBILIDADE_PADRAO) });
      showToast('Máquina cadastrada.', 'success');
      await onRecarregar();
    } catch (e) {
      showToast(mensagemDoErro(e) ?? 'Erro ao cadastrar a máquina.', 'error');
    }
  };

  return (
    <>
      <h4 className="mb-2 text-sm font-semibold text-content-primary">Recursos de balanceamento</h4>
      <p className="mb-3 text-xs text-content-muted">
        São estes que recebem carga e têm capacidade. Renomear um deles acerta junto as rotas, os lotes e o
        mix já escolhido — a tela mostra quanto antes de confirmar.
      </p>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-theme-border text-xs uppercase tracking-wide text-content-muted">
              <th className="pb-2 pr-3 text-left">Recurso</th>
              <th className="pb-2 pr-3 text-left">Área crítica</th>
              <th className="pb-2 pr-3 text-right">Ordem</th>
              <th className="pb-2 pr-3 text-left">Ações</th>
            </tr>
          </thead>
          <tbody>
            {recursos.map((r) => (
              <tr key={r.id} className="border-b border-theme-border/50">
                <td className={CELL}>
                  <input
                    aria-label={`Nome do recurso ${r.name}`}
                    defaultValue={r.name}
                    key={r.name}
                    onBlur={(e) => pedirRename(r, e.target.value)}
                    className={INPUT}
                  />
                </td>
                <td className={CELL}>
                  <input
                    aria-label={`Área crítica de ${r.name}`}
                    defaultValue={r.critical}
                    onBlur={(e) => e.target.value !== r.critical && salvarCampo(r, 'critical', e.target.value)}
                    className={INPUT}
                  />
                </td>
                <td className={`${CELL} text-right`}>
                  <input
                    aria-label={`Ordem de ${r.name}`}
                    type="number"
                    defaultValue={r.order}
                    onBlur={(e) => Number(e.target.value) !== r.order && salvarCampo(r, 'order', e.target.value)}
                    className={`${INPUT} text-right`}
                  />
                </td>
                <td className={CELL}>
                  <button
                    type="button"
                    data-testid={`seop-excluir-recurso-${r.name}`}
                    onClick={() => onExcluirRecurso(r)}
                    className="rounded-lg border border-theme-border px-2 py-1 text-xs text-theme-danger hover:bg-theme-danger/10"
                  >
                    Excluir
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-3 flex flex-wrap items-end gap-2">
        <label className="text-xs text-content-muted">
          Novo recurso
          <input
            aria-label="Nome do novo recurso"
            value={novoRecurso.name}
            onChange={(e) => setNovoRecurso({ ...novoRecurso, name: e.target.value })}
            className={`${INPUT} mt-1 min-w-56`}
          />
        </label>
        <label className="text-xs text-content-muted">
          Área crítica
          <input
            aria-label="Área crítica do novo recurso"
            value={novoRecurso.critical}
            onChange={(e) => setNovoRecurso({ ...novoRecurso, critical: e.target.value })}
            className={`${INPUT} mt-1 min-w-48`}
          />
        </label>
        <button
          type="button"
          data-testid="seop-adicionar-recurso"
          onClick={adicionarRecurso}
          className="rounded-lg bg-accent px-4 py-2 text-sm font-semibold text-accent-contrast hover:bg-accent-hover"
        >
          Cadastrar recurso
        </button>
        {erroNovo && (
          <span data-testid="seop-erro-recurso" className="text-xs text-theme-danger">
            {erroNovo}
          </span>
        )}
      </div>

      <h4 className="mb-2 mt-6 text-sm font-semibold text-content-primary">Recursos físicos (máquinas)</h4>
      <div className="mb-3 grid grid-cols-1 gap-3 md:grid-cols-4">
        {(
          [
            ['name', 'Máquina'],
            ['resource_group', 'Configuração'],
            ['critical', 'Área crítica'],
            ['availability', 'Disponibilidade %'],
          ] as const
        ).map(([campo, rotulo]) => (
          <label key={campo} className="text-xs text-content-muted">
            {rotulo}
            <input
              aria-label={`Nova máquina — ${rotulo}`}
              value={novaMaquina[campo]}
              onChange={(e) => setNovaMaquina({ ...novaMaquina, [campo]: e.target.value })}
              className={`${INPUT} mt-1`}
            />
          </label>
        ))}
      </div>
      <button
        type="button"
        data-testid="seop-adicionar-fisico"
        onClick={adicionarMaquina}
        className="rounded-lg bg-accent px-4 py-2 text-sm font-semibold text-accent-contrast hover:bg-accent-hover"
      >
        Cadastrar máquina
      </button>

      <div className="mt-4 overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-theme-border text-xs uppercase tracking-wide text-content-muted">
              <th className="pb-2 pr-3 text-left">Máquina</th>
              <th className="pb-2 pr-3 text-left">Configuração</th>
              <th className="pb-2 pr-3 text-left">Área crítica</th>
              <th className="pb-2 pr-3 text-right">Disponibilidade %</th>
              <th className="pb-2 text-left">
                <span className="sr-only">Excluir</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {fisicos.map((u) => (
              <tr key={u.id} className="border-b border-theme-border/50">
                {(
                  [
                    ['name', 'Nome'],
                    ['resource_group', 'Configuração'],
                    ['critical', 'Área crítica'],
                  ] as const
                ).map(([campo, rotulo]) => (
                  <td key={campo} className={CELL}>
                    <input
                      aria-label={`${rotulo} da máquina ${u.name}`}
                      defaultValue={u[campo]}
                      key={`${u.id}-${campo}-${u[campo]}`}
                      onBlur={(e) => e.target.value !== u[campo] && salvarMaquina(u, campo, e.target.value)}
                      className={INPUT}
                    />
                  </td>
                ))}
                <td className={`${CELL} text-right`}>
                  <input
                    aria-label={`Disponibilidade da máquina ${u.name}`}
                    type="number"
                    defaultValue={Math.round((u.availability ?? 0) * 100)}
                    key={`${u.id}-disp-${u.availability}`}
                    onBlur={(e) =>
                      Number(e.target.value) !== Math.round((u.availability ?? 0) * 100) &&
                      salvarMaquina(u, 'availability', e.target.value)
                    }
                    className={`${INPUT} text-right`}
                  />
                </td>
                <td className={CELL}>
                  <button
                    type="button"
                    data-testid={`seop-excluir-fisico-${u.id}`}
                    aria-label={`Excluir a máquina ${u.name}`}
                    onClick={() => excluirMaquina(u)}
                    className="rounded-lg p-1 text-content-muted hover:text-red-400"
                  >
                    <span className="material-symbols-outlined text-base">delete</span>
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <ConfirmModal
        open={!!renomeando}
        title={`Renomear ${renomeando?.recurso.name ?? ''}?`}
        confirmLabel="Renomear"
        tone="accent"
        isBusy={salvando}
        onConfirm={confirmarRename}
        onCancel={() => setRenomeando(null)}
      >
        <p data-testid="seop-impacto-rename">
          O novo nome será <strong>{renomeando?.novo}</strong>.
          {renomeando?.impacto ? (
            <>
              {' '}
              A troca acompanha <strong>{renomeando.impacto.rotas} etapa(s) de roteiro</strong>,{' '}
              <strong>{renomeando.impacto.lotes} parâmetro(s) de lote</strong>,{' '}
              <strong>{renomeando.impacto.alocacoes} mix(es) já escolhido(s)</strong> e{' '}
              <strong>{renomeando.impacto.cenarios} cenário(s) salvo(s)</strong>.
            </>
          ) : null}
        </p>
        <p>
          O nome é a referência usada em todo o plano: a troca é feita em cascata, de uma vez, para que
          nenhuma parte fique apontando para o nome antigo.
        </p>
      </ConfirmModal>
    </>
  );
};

export default EditorDeRecursos;
